/**
 * כיווניות — שורש התקלה שפגעה בתמליל המקור (A-20, MASTER §ה).
 *
 * הכלל: העמוד כולו `dir="rtl"`, וכל ריצה של טקסט שאינו עברי — אנגלית,
 * מספרים, תאריכים, דוא״ל, טלפון, מספרי סעיפים — נעטפת ביחידת בידוד
 * (`<bdi>` / `unicode-bidi: isolate`) כדי שלא תתהפך בתוך משפט עברי.
 *
 * הפיצול נעשה כאן, בשכבה טהורה אחת, כדי שהממשק ודוח ה־PDF יקבלו
 * בדיוק את אותה התנהגות. זו הסיבה שהפונקציה אינה מחזירה HTML.
 */

export interface BidiRun {
  text: string;
  /** true = יש לעטוף ביחידת בידוד. */
  isolate: boolean;
  /**
   * הכיוון שיש לכפות על היחידה המבודדת.
   * `ltr` לריצות לטיניות/מספריות; `rtl` לאסימונים עבריים־מעורבים
   * כמו "2א(ב)(3)" שבהם הסוגריים חייבים להישאר בסדר עברי.
   */
  dir: 'ltr' | 'rtl' | 'auto';
}

/**
 * מילה עברית, לרבות גרשיים פנימיים ("מ״ר", "ק״ת"). תפקידה לבלוע מילים
 * עבריות כך שהאסימונים האחרים לא ייתפסו בתוכן — היא עצמה לעולם אינה
 * מבודדת.
 *
 * הטווח הוא **אותיות** עבריות בלבד. מקף מחבר (־) נמצא אף הוא בבלוק
 * העברי, ולכן טווח רחב כמו `[֐-׿]` בלע אותו ואיתו את תחילת המספר
 * שאחריו: "ב־02.09.2026" התפצל ל־"ב־02" ול־"09.2026" והתאריך הוצג
 * שבור. הבאג נמצא בבדיקה החזותית של שלב 1.
 */
const HEBREW_WORD = /[א-ת]+(?:[״'"][א-ת]+)*/;

/** מספר סעיף עברי־מעורב: "2א(ב)(3)". מבודד ב־rtl כדי שהסוגריים לא יתהפכו. */
const SECTION_REF = /[0-9]+[א-ת][0-9()א-ת]*/;

/**
 * ריצה לטינית/מספרית עם סימני פיסוק פנימיים: דוא״ל, כתובת, תאריך,
 * מספר עם פסיקים, טלפון, מספר תקן.
 *
 * סימן האחוז נכלל כסיומת מותרת. כשהוא נשאר מחוץ ליחידה המבודדת הוא
 * הופך לתו ניטרלי ועובר לצד השני של המספר — "125%" הוצג "%125".
 * הבאג נמצא בבדיקה חזותית של צופה התוכנית בשלב 1.
 */
const LATIN_NUMERIC_RUN = /[A-Za-z0-9][A-Za-z0-9.,:@/+&#_%-]*[A-Za-z0-9%]|[A-Za-z0-9]%?/;

const SECTION_TOKEN = new RegExp(
  `(?:${HEBREW_WORD.source})|(?:${SECTION_REF.source})|(?:${LATIN_NUMERIC_RUN.source})`,
  'g',
);

function hasHebrew(value: string): boolean {
  return /[א-ת]/.test(value);
}

function hasLatinOrDigit(value: string): boolean {
  return /[A-Za-z0-9]/.test(value);
}

/**
 * מפצל טקסט לריצות, ומסמן אילו מהן חייבות בידוד דו־כיווני.
 * טקסט עברי טהור אינו מבודד — אין בכך צורך והוא רק מנפח את הפלט.
 */
export function splitBidiRuns(input: string): BidiRun[] {
  if (input === '') return [];

  const runs: BidiRun[] = [];
  let lastIndex = 0;

  SECTION_TOKEN.lastIndex = 0;
  for (const match of input.matchAll(SECTION_TOKEN)) {
    const token = match[0];
    const index = match.index ?? 0;

    // אסימון עברי טהור ללא ספרות אינו זקוק לבידוד.
    if (hasHebrew(token) && !hasLatinOrDigit(token)) continue;

    if (index > lastIndex) {
      runs.push({ text: input.slice(lastIndex, index), isolate: false, dir: 'auto' });
    }
    runs.push({
      text: token,
      isolate: true,
      dir: hasHebrew(token) ? 'rtl' : 'ltr',
    });
    lastIndex = index + token.length;
  }

  if (lastIndex < input.length) {
    runs.push({ text: input.slice(lastIndex), isolate: false, dir: 'auto' });
  }

  return mergeAdjacentLatinRuns(runs);
}

/**
 * ״דבק״ ניטרלי: רווח וסימני פיסוק שאין בהם אות עברית.
 *
 * סוגריים **אינם** דבק בכוונה. מיזוג על פני סוגריים היה הופך ביטוי כמו
 * "(3) (4)" ליחידה לטינית אחת ומזיז את הסוגריים. הדבק גם מוגבל באורכו
 * כדי שלא ייווצר מיזוג על פני מרחק שאינו באמת ביטוי אחד.
 */
const NEUTRAL_GLUE = /^[ \t,;:.'"–—·/+&|-]{1,3}$/;

/**
 * שתי ריצות לטיניות שמפריד ביניהן רווח או פיסוק בלבד הן **ביטוי אחד**,
 * ולכן חייבות בידוד אחד משותף.
 *
 * בלי המיזוג הזה כל מילה מבודדת בנפרד, והאלגוריתם הדו־כיווני מסדר את
 * המילים לפי כיוון הפסקה — כך ש־"Hakshuri Safety" התהפך והוצג
 * "Safety Hakshuri". זו בדיוק תקלת הכיווניות ששלב 1 נועד לתפוס, והיא
 * נתפסה בבדיקה החזותית של עמוד השער.
 */
function mergeAdjacentLatinRuns(runs: BidiRun[]): BidiRun[] {
  const merged: BidiRun[] = [];

  for (const run of runs) {
    const previous = merged[merged.length - 1];
    const beforePrevious = merged[merged.length - 2];

    const isLtrIsolate = run.isolate && run.dir === 'ltr';
    const previousIsGlue =
      previous !== undefined && !previous.isolate && NEUTRAL_GLUE.test(previous.text);
    const beforeIsLtrIsolate =
      beforePrevious !== undefined && beforePrevious.isolate && beforePrevious.dir === 'ltr';

    if (isLtrIsolate && previousIsGlue && beforeIsLtrIsolate) {
      beforePrevious.text += previous.text + run.text;
      merged.splice(merged.length - 1, 1);
      continue;
    }

    merged.push({ ...run });
  }

  return merged;
}

/**
 * אסימון קצר — תאריך, מספר סעיף, מזהה דוח, מספר עם פסיקים — לא יישבר
 * בין שורות. שבירה כזו מציגה "(Q-" בסוף שורה ו־"11, Q-12)" בתחילת
 * הבאה, ומבלבלת בדיוק במקום שבו הדיוק חשוב. ריצות ארוכות (דוא״ל,
 * כתובת) כן רשאיות להישבר, אחרת הן חורגות מרוחב התא.
 */
const KEEP_TOGETHER_MAX_LENGTH = 24;

export function shouldKeepTogether(run: BidiRun): boolean {
  return run.isolate && run.text.length <= KEEP_TOGETHER_MAX_LENGTH;
}

const HTML_ESCAPES: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
};

/** מניעת הזרקת HTML לתבנית הדוח (§9). כל טקסט שהמשתמש הזין עובר כאן. */
export function escapeHtml(input: string): string {
  return input.replace(/[&<>"']/g, (char) => HTML_ESCAPES[char] ?? char);
}

/**
 * טקסט בטוח + מבודד לשילוב בתבנית ה־HTML של הדוח.
 * זו הפונקציה היחידה שדרכה טקסט משתמש נכנס לדוח.
 */
export function bidiHtml(input: string): string {
  return splitBidiRuns(input)
    .map((run) => {
      const safe = escapeHtml(run.text);
      if (!run.isolate) return safe;
      const dirAttr = run.dir === 'auto' ? '' : ` dir="${run.dir}"`;
      const nowrap = shouldKeepTogether(run) ? ' style="white-space:nowrap"' : '';
      return `<bdi${dirAttr}${nowrap}>${safe}</bdi>`;
    })
    .join('');
}

/** שורות מרובות: כל שורה מבודדת בנפרד, שבירת שורה נשמרת. */
export function bidiHtmlMultiline(input: string): string {
  return input
    .split('\n')
    .map((line) => bidiHtml(line))
    .join('<br />');
}
